import { useMemo } from 'react';
import type { AnalyticsDataPoint } from '../../components/ChartContainer';

export type UpsellOfferData = {
    accepted: number;
    total: number;
    rate: number;
    offerTitle: string;
};

export type UpsellTakeRateData = {
    overall: { accepted: number; total: number; rate: number };
    byOffer: Record<string, UpsellOfferData>;
    totalMainOrders: number;
};

export type UpsellTakeRateSummary = {
    overall: { accepted: number; total: number; rate: number };
    byOffer: Record<string, UpsellOfferData>;
    totalMainOrders: number;
    mainOrdersWithUpsell: number;
};

// Helper to aggregate upsell take rate data by granularity
function aggregateUpsellTakeRateByGranularity(
    data: Array<{ code: string; accepted: number; total: number }>,
    granularity: 'daily' | 'weekly' | 'monthly'
): AnalyticsDataPoint[] {
    if (granularity === 'daily' || !data.length) {
        return data.map(d => ({
            code: d.code,
            value: d.total > 0 ? (d.accepted / d.total) * 100 : 0,
            volume: d.accepted,
        }));
    }

    const grouped: { [key: string]: { accepted: number; total: number }[] } = {};

    data.forEach(point => {
        const date = new Date(point.code);
        let key: string;

        if (granularity === 'weekly') {
            // Get start of week (Monday)
            const startOfWeek = new Date(date);
            const day = startOfWeek.getUTCDay();
            const diff = startOfWeek.getUTCDate() - day + (day === 0 ? -6 : 1);
            startOfWeek.setUTCDate(diff);
            key = startOfWeek.toISOString().split('T')[0];
        } else if (granularity === 'monthly') {
            // Get start of month
            key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-01`;
        } else {
            key = point.code;
        }

        if (!grouped[key]) {
            grouped[key] = [];
        }
        grouped[key].push({ accepted: point.accepted, total: point.total });
    });

    // Aggregate grouped data - sum accepted and total, then recalculate rate
    return Object.entries(grouped)
        .map(([key, points]) => {
            const totalAccepted = points.reduce((sum, p) => sum + p.accepted, 0);
            const totalOrders = points.reduce((sum, p) => sum + p.total, 0);
            return {
                code: key,
                value: totalOrders > 0 ? (totalAccepted / totalOrders) * 100 : 0,
                volume: totalAccepted,
            };
        })
        .sort((a, b) => new Date(a.code).getTime() - new Date(b.code).getTime());
}

// Aggregate time series data into a summary
export function useUpsellTakeRateSummary(
    map: Map<string, UpsellTakeRateData> | undefined,
): UpsellTakeRateSummary | undefined {
    return useMemo(() => {
        if (!map || map.size === 0) return undefined;

        let totalMainOrders = 0;
        let totalOrdersWithUpsell = 0;
        const offerTotals: Record<string, { accepted: number; offerTitle: string }> = {};

        for (const [, data] of map) {
            totalMainOrders += data.totalMainOrders;
            totalOrdersWithUpsell += data.overall.accepted;

            for (const [offerId, offerData] of Object.entries(data.byOffer)) {
                if (!offerTotals[offerId]) {
                    offerTotals[offerId] = { accepted: 0, offerTitle: offerData.offerTitle };
                }
                offerTotals[offerId].accepted += offerData.accepted;
            }
        }

        const byOffer: Record<string, UpsellOfferData> = {};
        for (const [offerId, totals] of Object.entries(offerTotals)) {
            byOffer[offerId] = {
                accepted: totals.accepted,
                total: totalMainOrders,
                rate: totalMainOrders > 0 ? (totals.accepted / totalMainOrders) * 100 : 0,
                offerTitle: totals.offerTitle,
            };
        }

        return {
            overall: {
                accepted: totalOrdersWithUpsell,
                total: totalMainOrders,
                rate: totalMainOrders > 0 ? (totalOrdersWithUpsell / totalMainOrders) * 100 : 0,
            },
            byOffer,
            totalMainOrders,
            mainOrdersWithUpsell: totalOrdersWithUpsell,
        };
    }, [map]);
}

// Transform time series data for charting overall take rate
export function useUpsellTakeRateTimeSeries(
    map: Map<string, UpsellTakeRateData> | undefined,
    granularity: 'daily' | 'weekly' | 'monthly',
): AnalyticsDataPoint[] {
    return useMemo(() => {
        if (!map) return [];
        const rawData = Array.from(map.entries())
            .map(([code, data]) => ({
                code,
                accepted: data.overall.accepted,
                total: data.overall.total,
            }))
            .sort((a, b) => a.code.localeCompare(b.code));
        return aggregateUpsellTakeRateByGranularity(rawData, granularity);
    }, [map, granularity]);
}

// Transform summary data for offer comparison bar chart
export function useUpsellTakeRateByOffer(
    summary: UpsellTakeRateSummary | undefined,
): AnalyticsDataPoint[] {
    return useMemo(() => {
        if (!summary) return [];

        return Object.entries(summary.byOffer)
            .sort((a, b) => b[1].accepted - a[1].accepted)
            .map(([, offerData]) => ({
                code: offerData.offerTitle,
                value: offerData.rate,
                volume: offerData.accepted,
            }));
    }, [summary]);
}

// Get the top N offers from summary
export function useTopUpsellOffers(
    summary: UpsellTakeRateSummary | undefined,
    maxOffers: number = 5,
): Array<{ offerId: string; data: UpsellOfferData }> {
    return useMemo(() => {
        if (!summary) return [];

        return Object.entries(summary.byOffer)
            .sort((a, b) => b[1].accepted - a[1].accepted)
            .slice(0, maxOffers)
            .map(([offerId, data]) => ({ offerId, data }));
    }, [summary, maxOffers]);
}
